import clsx from "clsx";
import styles from "./SegmentedControl.module.scss";

/** `md` (default) is the form rhythm — `--ac-control-h` tall, `fs-base`, sat beside inputs. `sm` is the
 * compact rung for a dense modal grid (the off-day / travel form), `fs-2xs`. */
export type SegmentedSize = "sm" | "md";

/** `default` is sentence-case body. `mono` is uppercase mono, for a control whose siblings are mono field
 * labels (the off-day modal) — the voice comes from the surrounding form, not the control. */
export type SegmentedVariant = "default" | "mono";

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  /** Names the group for assistive tech (its `aria-label`). Callers keep rendering their own VISIBLE field
   * label above the control, exactly as a `Field` label sits above an input — this only names the group. */
  label: string;
  value: T;
  onChange: (next: T) => void;
  options: readonly SegmentedOption<T>[];
  size?: SegmentedSize;
  variant?: SegmentedVariant;
  /** Fill the container's width, splitting evenly between segments, instead of shrinking to content. The
   * default is content-width — a segmented control is as wide as its words, and a full-width bar of two equal
   * halves is a deliberate choice (see the offer form's Deal / Type toggles), not the norm. */
  stretch?: boolean;
  className?: string;
}

/**
 * A segmented control: a bordered pill track holding 2+ mutually-exclusive options, the chosen one filled
 * with ink (STYLE-GUIDE ruling 14 — a dark fill takes a white glyph).
 *
 * **Why this is a primitive.** It was hand-rolled four times — `.seg`/`.segBtn` (off-day modal),
 * `.pillToggle`/`.kindPill` (the show editor's Show/Festival and, twice, the offer form) and `.segmented`
 * (create-org) — and the copies drifted the way this file's roadmap predicts: only ONE of them carried the
 * content-width rule (a segmented control is `inline-flex`, but its parent form column is `align-items:
 * stretch`, so it was pulled to the full width in three of the four), and the a11y split three ways. Owning
 * the geometry and the group semantics here is what stops a fifth copy.
 *
 * **Content-width by default.** `inline-size: fit-content` + `align-self: flex-start` pins it to its words
 * inside a flex/grid form column; `stretch` opts into the full-width two-halves treatment where a caller
 * genuinely wants it.
 *
 * **Group + `aria-pressed`, not a radiogroup.** A choose-one toggle-button group is this codebase's idiom for
 * these (the filter/toggle-chip family), and it keeps `getByRole("button", …)` working across the call sites.
 * The visible label is the CALLER's — a `<span>` in the form's own label voice — so this control renders none
 * of its own text and takes `label` purely for the group's accessible name.
 */
export function SegmentedControl<T extends string>({
  label,
  value,
  onChange,
  options,
  size = "md",
  variant = "default",
  stretch,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={clsx(styles.root, className)}
      role="group"
      aria-label={label}
      data-size={size}
      data-variant={variant}
      data-stretch={stretch ? "" : undefined}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={styles.segment}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
